import React from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

interface DashboardSkeletonProps {
  isDarkMode: boolean;
}

export const Skeleton = ({ className }: { className?: string }) => (
  <div className={`rounded bg-gray-200 dark:bg-gray-700 ${className}`} />
);

export const AppointmentsSkeleton = ({
  isDarkMode,
}: {
  isDarkMode: boolean;
}) => (
  <Card
    className={`flex-1 flex flex-col transition-colors duration-300 ${
      isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
    }`}
  >
    <CardHeader>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Skeleton className="w-4 h-4" />
          <div>
            <Skeleton className="w-32 h-4 mb-1" />
            <Skeleton className="w-20 h-3" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Skeleton className="w-16 h-8" />
          <div className="flex items-center gap-2">
            <Skeleton className="w-8 h-8" />
            <Skeleton className="w-8 h-8" />
          </div>
        </div>
      </div>
      <Skeleton className="w-24 h-4 mt-2" />
    </CardHeader>
    <CardContent className="flex flex-col flex-1 pt-3 overflow-hidden">
      <div className="flex-1 overflow-y-auto scrollbar-hover-thin">
        <div
          className={`border rounded-lg overflow-hidden transition-colors duration-300 ${isDarkMode ? "bg-gray-800 border-gray-600" : "bg-white border-gray-200"}`}
        >
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
            <div
              key={i}
              className={`flex border-b last:border-b-0 transition-colors duration-300 ${isDarkMode ? "border-gray-600" : "border-gray-200"}`}
            >
              <div
                className={`w-16 p-3 border-r flex-shrink-0 transition-colors duration-300 ${isDarkMode ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-200"}`}
              >
                <Skeleton className="w-8 h-4" />
              </div>
              <div className="flex-1 p-3 min-h-[80px]">
                <div
                  className={`p-3 rounded-lg transition-all duration-200 ${isDarkMode ? "bg-blue-900/40" : "bg-blue-50"} space-y-2`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 space-y-1">
                      <Skeleton className="w-3/4 h-4" />
                      <Skeleton className="w-1/2 h-3" />
                    </div>
                    <Skeleton className="h-5 rounded-full w-14" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </CardContent>
  </Card>
);

export const TeamFilterSkeleton = ({ isDarkMode }: { isDarkMode: boolean }) => (
  <Card
    className={`transition-colors duration-300 ${
      isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
    }`}
  >
    <CardContent className="pt-4 pb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Skeleton className="w-5 h-5 rounded-full" />
          <div>
            <Skeleton className="w-32 h-4 mb-1" />
            <Skeleton className="w-48 h-3" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="w-24 h-8" />
          <Skeleton className="w-32 h-8" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export const StatsSkeleton = ({ isDarkMode }: { isDarkMode: boolean }) => (
  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
    {[1, 2, 3].map((i) => (
      <Card
        key={i}
        className={`transition-colors duration-300 ${
          isDarkMode
            ? "bg-gray-800 border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <Skeleton className="w-24 h-3 mb-2" />
              <Skeleton className="w-16 h-8 mb-2" />
              <Skeleton className="w-32 h-3" />
            </div>
            <Skeleton className="w-12 h-12 rounded-full" />
          </div>
        </CardContent>
      </Card>
    ))}
  </div>
);

export const NotificationsSkeleton = ({
  isDarkMode,
}: {
  isDarkMode: boolean;
}) => (
  <Card
    className={`transition-colors duration-300 ${
      isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
    }`}
  >
    <CardHeader>
      <Skeleton className="w-40 h-5 mb-1" />
      <Skeleton className="w-24 h-3" />
    </CardHeader>
    <CardContent className="space-y-3">
      {[1, 2, 3, 4, 5, 6, 7, 8].map((j) => (
        <div
          key={j}
          className={`flex items-center gap-3 p-3 rounded-lg transition-colors duration-300 ${isDarkMode ? "bg-gray-700/50" : "bg-gray-50"}`}
        >
          <Skeleton className="w-4 h-4 rounded-full" />
          <div className="flex-1 space-y-1">
            <Skeleton className="w-3/4 h-3" />
            <Skeleton className="w-1/2 h-2" />
          </div>
          <Skeleton className="w-8 h-2" />
        </div>
      ))}
    </CardContent>
    <div
      className={`p-4 border-t flex items-center justify-between ${isDarkMode ? "border-gray-700" : "border-gray-100"}`}
    >
      <Skeleton className="w-24 h-3" />
      <div className="flex gap-1">
        <Skeleton className="w-6 h-6 rounded" />
        <Skeleton className="w-6 h-6 rounded" />
      </div>
    </div>
  </Card>
);

export const ActiveConsultationsSkeleton = ({
  isDarkMode,
}: {
  isDarkMode: boolean;
}) => (
  <Card
    className={`h-full w-full flex flex-col transition-colors duration-300 ${
      isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
    }`}
  >
    <CardHeader>
      <Skeleton className="h-5 mb-1 w-44" />
      <Skeleton className="h-3 w-28" />
    </CardHeader>
    <CardContent className="flex-1 space-y-3">
      {[1, 2, 3, 4, 5, 6, 7].map((j) => (
        <div
          key={j}
          className={`p-3 rounded-lg border transition-colors duration-300 ${isDarkMode ? "bg-gray-900/50 border-gray-700" : "bg-gray-50/50 border-gray-100"} space-y-2`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Skeleton className="w-24 h-3" />
              <Skeleton className="w-12 h-4 rounded" />
            </div>
            <Skeleton className="w-16 h-4 rounded" />
          </div>
          <Skeleton className="w-3/4 h-2" />
        </div>
      ))}
    </CardContent>
    <div
      className={`p-4 border-t flex items-center justify-between ${isDarkMode ? "border-gray-700" : "border-gray-100"}`}
    >
      <Skeleton className="w-24 h-3" />
      <div className="flex gap-1">
        <Skeleton className="rounded h-7 w-7" />
        <Skeleton className="rounded h-7 w-7" />
        <Skeleton className="rounded h-7 w-7" />
      </div>
    </div>
  </Card>
);

export default function DashboardSkeleton({
  isDarkMode,
}: DashboardSkeletonProps) {
  return (
    <div
      className={`h-screen flex flex-col transition-colors duration-300 ${
        isDarkMode ? "bg-gray-900" : "bg-gray-50"
      }`}
    >
      <div
        className={`flex-1 p-4 overflow-auto transition-colors duration-300 ${
          isDarkMode ? "bg-gray-900" : "bg-gray-50"
        }`}
      >
        <div className="flex flex-col w-full h-full">
          {/* Dashboard Content - Two Column Layout */}
          <div className="flex flex-1 gap-4 overflow-hidden">
            {/* Left Sidebar - 1/3 width */}
            <div className="flex flex-col w-1/3 space-y-4 overflow-y-auto scrollbar-hover-thin">
              <AppointmentsSkeleton isDarkMode={isDarkMode} />
            </div>

            {/* Right Main Content - 2/3 width */}
            <div className="w-2/3 overflow-y-auto scrollbar-hover-thin">
              <div className="space-y-4">
                <TeamFilterSkeleton isDarkMode={isDarkMode} />
                <StatsSkeleton isDarkMode={isDarkMode} />

                {/* Inner Two-column section */}
                <div className="flex gap-4">
                  {/* Inner Left Column - Alerts & Warnings */}
                  <div className="w-1/2 space-y-4">
                    <NotificationsSkeleton isDarkMode={isDarkMode} />
                  </div>

                  {/* Inner Right Column - Active Consultations */}
                  <div className="w-1/2">
                    <ActiveConsultationsSkeleton isDarkMode={isDarkMode} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
